import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { MainContainer } from '../../../components/ContentWrapper';
import config from '../../../config';
import { qrCodeEndpoint } from '../../../api/endpoints';
import axiosInstant from '../../../api';
import { getLandingUrl, IQrEntry } from './constants';
import { WhiteBackground } from '../../../components/WhiteBackground';
import { PageTitle } from '../../../components/PageTitle';
import { DotLottleLoader } from '../../../components/Loader';
import { QrMenuContainer } from './QrMenuContainer';
import Qr from '../../../components/Qr/Qr';
import DefaultLogo from '../../../assets/svg/vertical-logo-full-dark.svg';
import { QrForm } from './QrForm';

const SingleQr = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<IQrEntry>();
  const [openEditModal, setOpenEditModal] = useState(false);

  const fetchQr = async () => {
    const url = `${config.API_URL}${qrCodeEndpoint}/${id}`;
    setIsLoading(true);

    try {
      const response = await axiosInstant.get(url);
      if (response.data) setData(response.data);
    } catch (err) {
      toast.error('Failed to fetch QR code entry');
    } finally {
      setIsLoading(false);
    }
  };

  const getEntry = (title: string, text: string) => (
    <Box className="flex flex-wrap items-baseline gap-2 text-text-2">
      <Typography variant="body2" component="span" className="!text-lg shrink-0">
        {title}:
      </Typography>
      <span className="break-all">{text}</span>
    </Box>
  );

  useEffect(() => {
    if (id) fetchQr();
  }, [id]);

  if (isLoading) return <DotLottleLoader />;
  if (!data) return null;
  return (
    <WhiteBackground>
      <MainContainer>
        <Box className="w-full flex flex-wrap justify-between items-start gap-2" marginTop={1}>
          <Box className="min-w-0 flex-1">
            <PageTitle title={data.name} />
          </Box>
          <Box className="flex gap-2 shrink-0">
            <QrMenuContainer
              entry={data}
              isDetailedPage
              onEdit={() => setOpenEditModal(true)}
              onUpdate={fetchQr}
              onRemove={() => navigate('/qr-generator')}
            />
          </Box>
        </Box>
        <Box className="flex flex-col md:flex-row w-full mt-6 gap-4">
          <Box className="flex flex-col w-full border border-border-token rounded-lg p-3 gap-2">
            {getEntry('Landing URL', getLandingUrl(data.id))}
            {getEntry('Destination URL', data.url)}
            {getEntry('Status', data.deactivated ? 'In-Active' : 'Active')}
            {getEntry('Description', data.description)}
            {/* disabling till cors issue resolved */}
            {false && data?.branded_image && (
              <Box className="flex gap-2 flex-col">
                <Typography
                  variant="subtitle2"
                  fontFamily="CircularStdBlack"
                  className="!text-lg text-text-2"
                >
                  Branded Image
                </Typography>
                <img
                  src={data?.branded_image}
                  alt="branded-logo"
                  className="h-[155px] w-[155px] object-cover rounded-lg"
                />
              </Box>
            )}
          </Box>
          <Box className="w-full flex items-center justify-center flex-col gap-2 border border-border-token rounded-lg p-3">
            <Qr url={getLandingUrl(data.id)} image={DefaultLogo} />
          </Box>
        </Box>
        {openEditModal && (
          <QrForm
            entry={data}
            onUpdate={fetchQr}
            onClose={() => setOpenEditModal(false)}
          />
        )}
      </MainContainer>
    </WhiteBackground>
  );
};

export default SingleQr;
